/**
 * Semantic Color Search — ported from SPA implementation.
 *
 * Provides natural-language color search with mood, temperature,
 * and WCAG compliance awareness. Queries like "calm blue for dark mode AAA"
 * are parsed into structured scoring criteria.
 */
import Color from 'colorjs.io';
import { checkAACompliance } from './accessibilityTools.js';
import { getNamedColorEntries } from './namedColorDB.js';
// ── Constants ───────────────────────────────────────────────────────────────
const DEFAULT_DARK_BACKGROUND = '#0f172a';
const DEFAULT_LIGHT_BACKGROUND = '#f8fafc';
const DEFAULT_COMPLIANCE = 'aa';
const HUE_KEYWORDS = new Map([
    ['red', '#ef4444'], ['ruby', '#db2777'], ['crimson', '#dc2626'],
    ['orange', '#f97316'], ['amber', '#f59e0b'], ['gold', '#fbbf24'],
    ['yellow', '#fde047'], ['chartreuse', '#bef264'], ['lime', '#84cc16'],
    ['green', '#22c55e'], ['emerald', '#10b981'], ['mint', '#34d399'],
    ['teal', '#14b8a6'], ['cyan', '#06b6d4'], ['azure', '#0ea5e9'],
    ['blue', '#3b82f6'], ['navy', '#1d4ed8'], ['indigo', '#6366f1'],
    ['violet', '#8b5cf6'], ['purple', '#a855f7'], ['magenta', '#d946ef'],
    ['pink', '#ec4899'], ['rose', '#f472b6'], ['brown', '#8b5a2b'],
    ['tan', '#d2b48c'], ['beige', '#f5f5dc'], ['peach', '#fec89a'],
    ['coral', '#fb7185'], ['neutral', '#64748b'], ['gray', '#6b7280'],
    ['grey', '#6b7280'], ['slate', '#475569'], ['charcoal', '#1f2937'],
    ['black', '#111827'], ['white', '#ffffff'],
]);
const MODIFIER_KEYWORDS = {
    calm: { tag: 'calm' }, soft: { tag: 'soft' }, gentle: { tag: 'soft' },
    muted: { tag: 'muted' }, subdued: { tag: 'muted' }, pastel: { tag: 'pastel' },
    vibrant: { tag: 'vibrant' }, bright: { tag: 'vibrant' }, bold: { tag: 'bold' },
    deep: { tag: 'deep' }, rich: { tag: 'deep' }, dark: { tag: 'dark' },
    light: { tag: 'light' }, airy: { tag: 'light' }, warm: { tag: 'warm' },
    cool: { tag: 'cool' }, neutral: { tag: 'neutral' }, earthy: { tag: 'earthy' },
};
const BACKGROUND_KEYWORDS = [
    { predicates: ['dark background', 'on dark', 'for dark ui', 'dark mode', 'dark theme'], value: DEFAULT_DARK_BACKGROUND, kind: 'dark' },
    { predicates: ['light background', 'on light', 'for light ui', 'light mode', 'light theme'], value: DEFAULT_LIGHT_BACKGROUND, kind: 'light' },
];
const COMPLIANCE_KEYWORDS = [
    { predicates: ['aaa', 'triple a', 'triple-a'], value: 'aaa' },
    { predicates: ['aa', 'double a', 'double-a'], value: 'aa' },
];
const WARM_RANGE = [350, 60];
const COOL_RANGE = [120, 260];
// ── Parsing helpers ─────────────────────────────────────────────────────────
function normalise(query) {
    return query.trim().toLowerCase();
}
function wrapHue(value) {
    const normalized = value % 360;
    return normalized < 0 ? normalized + 360 : normalized;
}
function hueDistance(h1, h2) {
    const diff = Math.abs(wrapHue(h1) - wrapHue(h2));
    return diff > 180 ? 360 - diff : diff;
}
function hueWithinRange(hue, start, end) {
    const normalizedHue = wrapHue(hue);
    if (start <= end)
        return normalizedHue >= start && normalizedHue <= end;
    return normalizedHue >= start || normalizedHue <= end;
}
function matchHue(tokens) {
    for (const token of tokens) {
        if (HUE_KEYWORDS.has(token))
            return HUE_KEYWORDS.get(token);
    }
    return undefined;
}
function detectModifiers(tokens) {
    const modifiers = new Set();
    for (const token of tokens) {
        const entry = MODIFIER_KEYWORDS[token];
        if (entry)
            modifiers.add(entry.tag);
    }
    return modifiers;
}
function parseBackgroundHint(query, fallback) {
    const normalized = normalise(query);
    for (const descriptor of BACKGROUND_KEYWORDS) {
        if (descriptor.predicates.some((p) => normalized.includes(p))) {
            return { background: descriptor.value, kind: descriptor.kind };
        }
    }
    if (fallback && fallback.trim())
        return { background: fallback, kind: 'custom' };
    return { background: DEFAULT_DARK_BACKGROUND, kind: 'dark' };
}
function parseCompliance(query, fallback) {
    const normalized = normalise(query);
    for (const descriptor of COMPLIANCE_KEYWORDS) {
        if (descriptor.predicates.some((p) => normalized.includes(p)))
            return descriptor.value;
    }
    return fallback;
}
function parseQuery(query, options) {
    const normalized = normalise(query);
    const tokens = normalized.split(/[^a-z0-9#]+/).filter(Boolean);
    const primaryHue = matchHue(tokens);
    const modifiers = detectModifiers(tokens);
    const compliance = parseCompliance(normalized, options?.target ?? DEFAULT_COMPLIANCE);
    const backgroundHint = parseBackgroundHint(query, options?.fallbackBackground);
    const tags = new Set();
    if (primaryHue) {
        const hueLabel = Array.from(HUE_KEYWORDS.entries()).find(([, hex]) => hex === primaryHue)?.[0];
        if (hueLabel)
            tags.add(hueLabel);
    }
    modifiers.forEach((m) => tags.add(m));
    if (backgroundHint.kind === 'dark')
        tags.add('dark-background');
    else if (backgroundHint.kind === 'light')
        tags.add('light-background');
    return {
        tokens,
        primaryHue,
        modifiers,
        compliance,
        background: backgroundHint.background,
        requestedBackgroundKind: backgroundHint.kind,
        tags: Array.from(tags),
    };
}
// ── Scoring ─────────────────────────────────────────────────────────────────
function calculateMoodPenalty(l, c, modifiers) {
    let penalty = 0;
    if (modifiers.has('calm') || modifiers.has('soft')) {
        penalty += Math.abs(c - 0.08) * 60;
        penalty += Math.abs(l - 0.7) * 15;
    }
    if (modifiers.has('muted') || modifiers.has('pastel')) {
        penalty += Math.max(0, c - 0.12) * 80;
        penalty += Math.abs(l - 0.75) * 20;
    }
    if (modifiers.has('vibrant') || modifiers.has('bold')) {
        penalty += Math.max(0, 0.18 - c) * 120;
    }
    if (modifiers.has('deep') || modifiers.has('dark')) {
        penalty += Math.max(0, l - 0.45) * 80;
    }
    if (modifiers.has('light') || modifiers.has('pastel')) {
        penalty += Math.max(0, 0.75 - l) * 80;
    }
    if (modifiers.has('neutral')) {
        penalty += Math.max(0, Math.abs(c - 0.04)) * 120;
    }
    if (modifiers.has('earthy')) {
        penalty += Math.abs(l - 0.6) * 20;
        penalty += Math.max(0, Math.abs(c - 0.1)) * 60;
    }
    return penalty;
}
function calculateTemperaturePenalty(h, modifiers) {
    let penalty = 0;
    if (modifiers.has('warm')) {
        if (!hueWithinRange(h, WARM_RANGE[0], WARM_RANGE[1])) {
            penalty += Math.min(hueDistance(h, WARM_RANGE[0]), hueDistance(h, WARM_RANGE[1])) * 0.5;
        }
    }
    if (modifiers.has('cool')) {
        if (!hueWithinRange(h, COOL_RANGE[0], COOL_RANGE[1])) {
            penalty += Math.min(hueDistance(h, COOL_RANGE[0]), hueDistance(h, COOL_RANGE[1])) * 0.5;
        }
    }
    return penalty;
}
function evaluateCandidate(entry, context) {
    const color = new Color(entry.hex);
    const oklch = color.to('oklch');
    const [l, c, h] = oklch.coords;
    let score = 0;
    let deltaE = null;
    const notes = [];
    if (context.primaryHue) {
        const target = new Color(context.primaryHue);
        deltaE = color.deltaE2000(target);
        score += deltaE;
    }
    else {
        score += 25;
    }
    score += calculateMoodPenalty(l, c, context.modifiers);
    score += calculateTemperaturePenalty(h, context.modifiers);
    if (context.modifiers.has('light'))
        score += Math.max(0, 0.8 - l) * 80;
    if (context.modifiers.has('dark'))
        score += Math.max(0, l - 0.3) * 80;
    const contrastResult = checkAACompliance(entry.hex, context.background, { textSizePx: 16, isBold: false });
    const contrastRatio = contrastResult?.ratio ?? null;
    const complianceLevel = contrastResult?.level ?? 'Fail';
    let meetsTarget = Boolean(contrastResult?.compliant);
    if (context.compliance === 'aaa' && complianceLevel !== 'AAA')
        meetsTarget = false;
    if (!meetsTarget) {
        score += 50;
        notes.push('Does not meet requested contrast target');
    }
    if (contrastRatio === null) {
        score += 30;
        notes.push('Contrast could not be calculated');
    }
    else if (context.compliance === 'aaa' && contrastRatio < 7) {
        notes.push(`Contrast ${contrastRatio.toFixed(2)}:1 below AAA threshold`);
    }
    else if (context.compliance === 'aa' && contrastRatio < 4.5) {
        notes.push(`Contrast ${contrastRatio.toFixed(2)}:1 below AA threshold`);
    }
    const tags = [...context.tags, entry.source];
    return {
        entry,
        score: Number(score.toFixed(3)),
        deltaE: deltaE !== null ? Number(deltaE.toFixed(2)) : null,
        contrastRatio: contrastRatio !== null ? Number(contrastRatio.toFixed(2)) : null,
        compliance: complianceLevel,
        meetsTarget,
        background: context.background,
        tags,
        notes,
    };
}
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
// ── Public API ──────────────────────────────────────────────────────────────
/**
 * Search named colors using natural-language queries.
 *
 * Examples:
 * - "calm blue for dark mode"
 * - "vibrant red AAA"
 * - "muted earthy green on light background"
 */
export async function semanticColorSearch(query, options) {
    const normalized = normalise(query);
    if (!normalized)
        return [];
    const context = parseQuery(query, options);
    const limit = clamp(options?.limit ?? 6, 1, 24);
    const entries = await getNamedColorEntries({ sources: options?.sources });
    const candidates = entries.map((entry) => evaluateCandidate(entry, context));
    // Post-process: prioritize passing, then sort by score
    const passing = candidates.filter((c) => c.meetsTarget);
    const failing = candidates.filter((c) => !c.meetsTarget);
    passing.sort((a, b) => a.score - b.score);
    failing.sort((a, b) => a.score - b.score);
    const ordered = [...passing, ...failing].slice(0, limit);
    return ordered.map((c) => ({
        id: c.entry.id,
        name: c.entry.displayName,
        hex: c.entry.hex,
        source: c.entry.source,
        score: c.score,
        deltaE: c.deltaE,
        contrastRatio: c.contrastRatio,
        compliance: c.compliance,
        meetsTarget: c.meetsTarget,
        background: c.background,
        tags: Array.from(new Set(c.tags)),
        notes: c.notes,
    }));
}
//# sourceMappingURL=semanticSearch.js.map